import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/button/button';
import { Checkbox } from '@/components/checkbox/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Spinner } from '@/components/spinner/spinner';
import { getListProductQueryOptions } from '@/features/products/api/get-list-product.api';

export interface VoucherProductSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
}

export default function VoucherProductSelector({
  value,
  onChange,
}: VoucherProductSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const productsQuery = useQuery({
    ...getListProductQueryOptions({ limit: 100, search }),
    enabled: open,
  });
  const products = productsQuery.data?.result.data ?? [];

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  };

  return (
    <div className="grid gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map(id => {
            const product = products.find(p => p.id === id);
            return (
              <span key={id} className="px-2 py-1 bg-muted rounded-md text-xs">
                {product?.title || id}
              </span>
            );
          })}
        </div>
      )}
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        {value.length > 0 ? 'Ubah Produk' : 'Pilih Produk'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pilih Produk</DialogTitle>
            <DialogDescription>
              Pilih produk yang berlaku untuk voucher ini.
            </DialogDescription>
          </DialogHeader>

          <Input
            placeholder="Cari produk..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <div className="max-h-[300px] overflow-y-auto">
            {productsQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="size-6" />
              </div>
            ) : products.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Tidak ada produk ditemukan
              </p>
            ) : (
              <div className="flex flex-col gap-1">
                {products.map(product => {
                  const checked = value.includes(product.id);
                  return (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => toggle(product.id)}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-muted text-left"
                    >
                      <Checkbox
                        checked={checked}
                        onClick={e => e.preventDefault()}
                      />
                      <span className="flex-1 truncate">
                        {product.title || product.id}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" onClick={() => setOpen(false)}>
              Selesai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
